import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FilterListIcon from '@mui/icons-material/FilterList';
import { SearchInput } from 'components';
import { useSelector, useDispatch } from 'react-redux';

import { NavLink } from 'react-router-dom';
import { authAdminAndGroupAdminMiddleWare } from 'util/authAdmin'
import { useHistory } from 'react-router-dom';
import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage'
import { updateRestaurantkeyword } from 'store/actions/restaurant';
import { updateUserRestaurantId } from 'store/actions/account';
import API from 'util/API';
import { checkMenuAdmin } from 'util/userUtils';

const useStyles = makeStyles(theme => ({
  root: {},
  row: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      // A fixed-height, non-wrapping row clips its buttons on a 344px foldable.
      height: 'auto',
      flexWrap: 'wrap',
      gap: theme.spacing(1)
    }
  },
  spacer: {
    flexGrow: 1,
    // A growing spacer inside a wrapping row renders as a blank line.
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  importButton: {
    marginRight: theme.spacing(1)
  },
  exportButton: {
    marginRight: theme.spacing(1)
  },
  rightSpacer: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  searchInput: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      width: '100%'
    }
  },
  actionLink: {
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      '& button': {
        width: '100%'
      }
    }
  },
  searchRow: {
    [theme.breakpoints.down('sm')]: {
      display: 'block'
    }
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  filterIcon: {
    color: theme.palette.text.secondary,
  },
  filterControl: {
    minWidth: 140,
    // Two controls per row on a phone instead of one long wrapping strip.
    [theme.breakpoints.down('sm')]: {
      minWidth: 0,
      flex: '1 1 40%',
    }
  },
  clearButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
}));

const RestaurantsToolbar = props => {
  const {
    className,
    statusFilter,
    onStatusFilterChange,
    paymentFilter,
    onPaymentFilterChange,
    deliveryProviderFilter,
    onDeliveryProviderFilterChange,
    terminalFilter,
    onTerminalFilterChange,
    kdsFilter,
    onKdsFilterChange,
    ...rest
  } = props;
  const [uiLoading, setUiLoading] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector((state) => state.account.user);
  const selectedRestaurants = useSelector((state) => state.restaurant.selectedRestaurantIds);
  const showSuccessMessage = (msg) => dispatch(showSuccessSnackbar(msg));
  const showErrorMessage = (msg) => dispatch(showErrorSnackbar(msg));
  const updateFilter = (key) => dispatch(updateRestaurantkeyword(key));

  const classes = useStyles();


  const accessRestaurant = () => {
    console.log('selectedRestaurants', selectedRestaurants);
    if (selectedRestaurants.length !== 1) {
      return;
    }
    authAdminAndGroupAdminMiddleWare(history, user);
    // const authToken = localStorage.getItem('AuthToken');
    // API.defaults.headers.common['Authorization'] = authToken;
    setUiLoading(true);
    API
      .put('/userRestaurantId', { restaurantId: selectedRestaurants[0] })
      .then((response) => {
        setUiLoading(false);
        dispatch(updateUserRestaurantId(selectedRestaurants[0]));
        showSuccessMessage("Updated successfully!");
      })
      .catch((error) => {
        console.log(error);
        setUiLoading(false);
        if (error.response && error.response.status === 403) {
          history.push('/sign-in');
        }

        showErrorMessage('Error in updating the data');

      });


  };

  const filterRestaurant = (e) => {
     updateFilter(e.target.value);
  };

  // checkMenuAdmin is true for full admins too.
  const canFilterOps = checkMenuAdmin(user);

  // One definition drives the controls, the active count and the clear button,
  // so adding a filter here cannot leave the reset out of sync.
  const filterControls = [
    {
      label: 'Status',
      value: statusFilter,
      onChange: onStatusFilterChange,
      options: [
        { value: 'all', label: 'All' },
        { value: 'open', label: 'Open' },
        { value: 'closed', label: 'Closed' },
      ],
    },
    {
      label: 'Online Payment',
      value: paymentFilter,
      onChange: onPaymentFilterChange,
      options: [
        { value: 'all', label: 'All' },
        { value: 'hasPayment', label: 'Has payment' },
        { value: 'noPayment', label: 'No payment' },
      ],
    },
    {
      label: 'Delivery',
      value: deliveryProviderFilter,
      onChange: onDeliveryProviderFilterChange,
      options: [
        { value: 'all', label: 'All' },
        { value: 'uberEats', label: 'UberEats' },
        { value: 'none', label: 'None' },
      ],
    },
    // Terminal and KDS mirror table columns that only admin and menuAdmin
    // list responses carry, so their filters follow the same roles.
    ...(canFilterOps ? [
      {
        label: 'Terminal',
        value: terminalFilter,
        onChange: onTerminalFilterChange,
        options: [
          { value: 'all', label: 'All' },
          { value: 'Adyen', label: 'Adyen' },
          { value: 'ANZ', label: 'ANZ' },
          { value: 'Other', label: 'Other' },
        ],
      },
      {
        label: 'KDS',
        value: kdsFilter,
        onChange: onKdsFilterChange,
        options: [
          { value: 'all', label: 'All' },
          { value: 'on', label: 'On' },
          { value: 'off', label: 'Off' },
        ],
      },
    ] : []),
  ];

  const activeFilterCount = filterControls.filter(
    (control) => (control.value || 'all') !== 'all'
  ).length;

  const clearFilters = () => {
    filterControls.forEach((control) => {
      if (control.onChange) {
        control.onChange('all');
      }
    });
  };

  return (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >
      <div className={classes.row}>
        <span className={classes.spacer} />
        {/* <IconButton aria-label="delete">
          <DeleteIcon />
        </IconButton> */}
   

        <IconButton aria-label="acccess restaurant" onClick={accessRestaurant}
          disabled={uiLoading} >
          <TransferWithinAStationIcon />
        </IconButton>


       { user && user.role === 'admin' ?
       (
         <>
        <NavLink to={'/restaurantGroup'} className={classes.actionLink}>
        <Button
          color="primary"
          variant="contained"
        >
          Add Restaurant Group
      </Button>
      </NavLink>
      <span className={classes.rightSpacer} />
        <NavLink to={'/restaurant'} className={classes.actionLink}>
          <Button
            color="primary"
            variant="contained"
          >
            Add Restaurant
        </Button>
        </NavLink>
        </>
        ) : null }
      </div>
      <div className={clsx(classes.row, classes.searchRow)}>
        <SearchInput
          className={classes.searchInput}
          placeholder="Search restaurant"
          onChange={filterRestaurant}
        />
      </div>

      <div className={classes.filterRow}>
        <FilterListIcon fontSize="small" className={classes.filterIcon} />
        {filterControls.map((control) => {
          // aria-labelledby is a space-separated id list, so the id must not
          // contain the label's spaces.
          const labelId = `${control.label.toLowerCase().replace(/\s+/g, '-')}-filter-label`;
          return (
            // The theme forces every MuiOutlinedInput to 56px/full-width with
            // !important (theme/overrides/MuiOutlinedInput.js), which mangles a
            // compact outlined Select - the standard variant is untouched by it.
            <FormControl
              key={control.label}
              size="small"
              variant="standard"
              className={classes.filterControl}
            >
              <InputLabel id={labelId}>{control.label}</InputLabel>
              <Select
                labelId={labelId}
                value={control.value || 'all'}
                onChange={(e) => control.onChange && control.onChange(e.target.value)}
              >
                {control.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        })}
        {activeFilterCount > 0 && (
          <Button
            size="small"
            color="primary"
            className={classes.clearButton}
            onClick={clearFilters}
          >
            Clear filters ({activeFilterCount})
          </Button>
        )}
      </div>

    </div>
  );
};

RestaurantsToolbar.propTypes = {
  className: PropTypes.string,
  statusFilter: PropTypes.string,
  onStatusFilterChange: PropTypes.func,
  paymentFilter: PropTypes.string,
  onPaymentFilterChange: PropTypes.func,
  deliveryProviderFilter: PropTypes.string,
  onDeliveryProviderFilterChange: PropTypes.func,
  terminalFilter: PropTypes.string,
  onTerminalFilterChange: PropTypes.func,
  kdsFilter: PropTypes.string,
  onKdsFilterChange: PropTypes.func,
};

export default RestaurantsToolbar;
